import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IDismantlingBaySettingResponse,
  IDismantlingBaySettingsResponse,
  IUpsertDismantlingBaySettingRequest,
} from "../models/DismantlingBay";

interface UseDismantlingBaySettingArgs {
  dismantlingBayId?: string;
  date?: string;
  from?: string;
  to?: string;
  enabled?: boolean;
}

export default function useDismantlingBaySetting({
  dismantlingBayId,
  date,
  from,
  to,
  enabled = true,
}: UseDismantlingBaySettingArgs = {}) {
  const { get, put } = useApiHelper();
  const queryClient = useQueryClient();

  const setting = useQuery({
    queryKey: ["dismantling-bay-setting", dismantlingBayId, date],
    queryFn: () =>
      get<IDismantlingBaySettingResponse>(
        `/dismantlingbaysetting/${dismantlingBayId}?date=${date}`
      ),
    enabled: enabled && !!dismantlingBayId && !!date,
    staleTime: 1000 * 60,
  });

  const settingsRange = useQuery({
    queryKey: ["dismantling-bay-settings-range", from, to],
    queryFn: () =>
      get<IDismantlingBaySettingsResponse>(
        `/dismantlingbaysetting?from=${from}&to=${to}`
      ),
    enabled: enabled && !!from && !!to,
    staleTime: 1000 * 60,
  });

  const upsertSetting = useMutation(
    ({
      dismantlingBayId: bayId,
      date: targetDate,
      body,
    }: {
      dismantlingBayId: string,
      date: string,
      body: IUpsertDismantlingBaySettingRequest,
    }) =>
      put<IUpsertDismantlingBaySettingRequest, IDismantlingBaySettingResponse>(
        `/dismantlingbaysetting/${bayId}?date=${targetDate}`,
        body
      ),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "dismantling-bay-setting",
          variables.dismantlingBayId,
          variables.date,
        ]);
        queryClient.invalidateQueries(["dismantling-bay-settings-range"]);
      },
    }
  );

  return {
    setting,
    settingsRange,
    upsertSetting,
  };
}
